import { useState, useRef, useEffect } from 'react';
import { Folder } from '../../api';
import { getErrorMessage } from '../../../../shared/src';

interface BreadcrumbProps {
  path: Folder[];
  onNavigate: (folderId: string | null) => void;
  onNavigateRoot: () => void;
  onFileDrop?: (folderId: string | null, fileId: string) => void;
  onFolderDrop?: (targetFolderId: string | null, movedFolderId: string) => void;
  onMultiFileDrop?: (targetFolderId: string | null, fileIds: string[]) => void;
  onMultiFolderDrop?: (targetFolderId: string | null, folderIds: string[]) => void;
  onCreateFolder?: (name: string) => Promise<string>;
}

export function Breadcrumb({ path, onNavigate, onNavigateRoot, onFileDrop, onFolderDrop, onMultiFileDrop, onMultiFolderDrop, onCreateFolder }: BreadcrumbProps) {
  const [dragOverId, setDragOverId] = useState<string | null | 'root'>(null);
  // Track drag enter/leave counts to handle nested elements properly
  const dragCounters = useRef<Map<string, number>>(new Map());

  // Inline folder creation state
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [createError, setCreateError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering create mode
  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  const startCreating = () => {
    setIsCreating(true);
    setNewFolderName('');
    setCreateError('');
  };

  const cancelCreating = () => {
    setIsCreating(false);
    setNewFolderName('');
    setCreateError('');
  };

  const validateFolderName = (name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return null; // Empty is not an error, just cancel
    if (trimmed.length > 255) return 'Name too long (max 255 characters)';
    if (/[<>:"/\\|?*]/.test(trimmed)) return 'Invalid characters in name';
    return ''; // Valid
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed || !onCreateFolder) {
      cancelCreating();
      return;
    }

    const validationResult = validateFolderName(trimmed);
    if (validationResult === null) {
      cancelCreating();
      return;
    }
    if (validationResult) {
      setCreateError(validationResult);
      return;
    }

    setIsSubmitting(true);
    try {
      const newFolderId = await onCreateFolder(trimmed);
      setIsCreating(false);
      setNewFolderName('');
      setCreateError('');
      // Navigate into the new folder
      onNavigate(newFolderId);
    } catch (err: unknown) {
      setCreateError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCreating();
    }
  };

  const handleCreateBlur = () => {
    // Only cancel if not submitting (to avoid race condition)
    if (!isSubmitting) {
      cancelCreating();
    }
  };

  const hasDraggableItem = (e: React.DragEvent) =>
    e.dataTransfer.types.includes('application/x-file-id') ||
    e.dataTransfer.types.includes('application/x-folder-id') ||
    e.dataTransfer.types.includes('application/x-file-ids') ||
    e.dataTransfer.types.includes('application/x-folder-ids');

  const handleDragEnter = (e: React.DragEvent, id: string | 'root') => {
    e.preventDefault();
    if (hasDraggableItem(e)) {
      const count = (dragCounters.current.get(id) || 0) + 1;
      dragCounters.current.set(id, count);
      setDragOverId(id);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (hasDraggableItem(e)) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e: React.DragEvent, id: string | 'root') => {
    e.preventDefault();
    const count = (dragCounters.current.get(id) || 1) - 1;
    dragCounters.current.set(id, count);
    if (count <= 0) {
      dragCounters.current.set(id, 0);
      if (dragOverId === id) {
        setDragOverId(null);
      }
    }
  };

  const handleDrop = (e: React.DragEvent, folderId: string | null, id: string | 'root') => {
    e.preventDefault();
    e.stopPropagation();
    dragCounters.current.set(id, 0);
    setDragOverId(null);

    // Multi-file drop
    const multiFileIds = e.dataTransfer.getData('application/x-file-ids');
    if (multiFileIds && onMultiFileDrop) {
      const ids = multiFileIds.split(',');
      onMultiFileDrop(folderId, ids);
      return;
    }

    // Single file drop
    const fileId = e.dataTransfer.getData('application/x-file-id');
    if (fileId && onFileDrop) {
      onFileDrop(folderId, fileId);
      return;
    }

    // Multi-folder drop
    const multiFolderIds = e.dataTransfer.getData('application/x-folder-ids');
    if (multiFolderIds && onMultiFolderDrop) {
      const ids = multiFolderIds.split(',').filter((mid) => mid !== folderId);
      if (ids.length > 0) {
        onMultiFolderDrop(folderId, ids);
      }
      return;
    }

    // Single folder drop
    const movedFolderId = e.dataTransfer.getData('application/x-folder-id');
    if (movedFolderId && onFolderDrop) {
      onFolderDrop(folderId, movedFolderId);
    }
  };

  const isAtRoot = path.length === 0;

  return (
    <nav className="breadcrumb">
      {isAtRoot ? (
        <span className="breadcrumb-item breadcrumb-root breadcrumb-current">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path d="M9 22V12h6v10" />
          </svg>
          <span>Home</span>
        </span>
      ) : (
        <button
          className={`breadcrumb-item breadcrumb-root ${dragOverId === 'root' ? 'breadcrumb-drag-over' : ''}`}
          onClick={onNavigateRoot}
          onDragEnter={(e) => handleDragEnter(e, 'root')}
          onDragOver={handleDragOver}
          onDragLeave={(e) => handleDragLeave(e, 'root')}
          onDrop={(e) => handleDrop(e, null, 'root')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path d="M9 22V12h6v10" />
          </svg>
          <span>Home</span>
        </button>
      )}

      {path.map((folder, index) => (
        <span key={folder.id} className="breadcrumb-segment">
          <span className="breadcrumb-separator">/</span>
          {index === path.length - 1 ? (
            <span className="breadcrumb-item breadcrumb-current">{folder.name}</span>
          ) : (
            <button
              className={`breadcrumb-item ${dragOverId === folder.id ? 'breadcrumb-drag-over' : ''}`}
              onClick={() => onNavigate(folder.id)}
              onDragEnter={(e) => handleDragEnter(e, folder.id)}
              onDragOver={handleDragOver}
              onDragLeave={(e) => handleDragLeave(e, folder.id)}
              onDrop={(e) => handleDrop(e, folder.id, folder.id)}
            >
              {folder.name}
            </button>
          )}
        </span>
      ))}

      {onCreateFolder && (
        isCreating ? (
          <span className="breadcrumb-segment breadcrumb-create">
            <span className="breadcrumb-separator">/</span>
            <input
              ref={createInputRef}
              type="text"
              className={`breadcrumb-create-input ${createError ? 'input-error' : ''}`}
              value={newFolderName}
              onChange={(e) => {
                setNewFolderName(e.target.value);
                setCreateError('');
              }}
              onKeyDown={handleCreateKeyDown}
              onBlur={handleCreateBlur}
              placeholder="New folder"
              disabled={isSubmitting}
            />
          </span>
        ) : (
          <button
            className="breadcrumb-add-folder"
            onClick={startCreating}
            title="New folder"
          >
            /
          </button>
        )
      )}
    </nav>
  );
}
